import {publicFetch, resolvePublicApiBaseUrl} from '@fitro/customer-host/api'

export function supportsPublicOnlineBooking(offer: PublicPrivateBookingOffer): boolean {
    return offer.paymentMode === 'online_upfront'
        || offer.paymentMode === 'pass_or_online'
        || offer.paymentMode === 'on_site_or_online'
        || offer.paymentMode === 'pass_or_online_or_on_site'
}

export type PublicPrivateBookingInstructor = {
    avatarUrl?: string | null
    firstName?: string | null
    id: string
    lastName?: string | null
}

export type PublicPrivateBookingLocation = {
    id: string
    name: string
}

export type PublicPrivateBookingOffer = {
    allowedInstructorIds: string[]
    allowedLocationIds: string[]
    allowedPassTemplateIds: string[]
    allowedPassTemplates?: PublicPrivateBookingPassTemplate[]
    archivedAt?: string | null
    bookingHorizonDays: number
    bookingMode: string
    bufferAfterMinutes: number
    bufferBeforeMinutes: number
    channels: string[]
    createdAt?: string | null
    currency: string
    description?: string | null
    durationMinutes: number
    /** Whether a visitor may book without an account; otherwise they sign in first. */
    guestCheckoutAvailable?: boolean
    id: string
    instructors: PublicPrivateBookingInstructor[]
    locations?: PublicPrivateBookingLocation[]
    cancellationDeadlineMinutes?: number
    minLeadTimeMinutes: number
    name: string
    paymentMode: string
    priceMinor: number
    publishedAt?: string | null
    slotStepMinutes: number
    status: string
    trainerConfirmationDeadlineHours?: number | null
    trainerConfirmationRequired: boolean
    trainerSelectionMode: string
    updatedAt?: string | null
    visibleToCustomers: boolean
}

export type PublicPrivateBookingPassTemplate = {
    id: string
    name: string
}

export type PublicPrivateBookingSlot = {
    availableInstructorCount: number
    endAt: string
    instructorId?: string | null
    locationId?: string | null
    startAt: string
}

export type PublicPrivateBookingSlotsResponse = {
    diagnostics?: unknown[]
    from: string
    offerId: string
    slots: PublicPrivateBookingSlot[]
    to: string
}

const EMPTY_PUBLIC_PRIVATE_BOOKING_OFFERS: PublicPrivateBookingOffer[] = []

function isString(value: unknown): value is string {
    return typeof value === 'string'
}

function isOptionalString(value: unknown): value is string | null | undefined {
    return value === undefined || value === null || typeof value === 'string'
}

function isNonNegativeInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

function isPositiveInteger(value: unknown): value is number {
    return isNonNegativeInteger(value) && value > 0
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(isString)
}

function isPublicPrivateBookingInstructor(value: unknown): value is PublicPrivateBookingInstructor {
    if (!value || typeof value !== 'object') return false
    const candidate = value as PublicPrivateBookingInstructor
    return isString(candidate.id)
        && isOptionalString(candidate.avatarUrl)
        && isOptionalString(candidate.firstName)
        && isOptionalString(candidate.lastName)
}

function isPublicPrivateBookingLocation(value: unknown): value is PublicPrivateBookingLocation {
    if (!value || typeof value !== 'object') return false
    const candidate = value as PublicPrivateBookingLocation
    return isString(candidate.id) && isString(candidate.name) && candidate.name.trim().length > 0
}

function isPublicPrivateBookingOffer(value: unknown): value is PublicPrivateBookingOffer {
    if (!value || typeof value !== 'object') return false
    const candidate = value as PublicPrivateBookingOffer
    return isString(candidate.id)
        && isString(candidate.name)
        && candidate.name.trim().length > 0
        && isOptionalString(candidate.description)
        && isPositiveInteger(candidate.durationMinutes)
        && isNonNegativeInteger(candidate.priceMinor)
        && isString(candidate.currency)
        && isString(candidate.bookingMode)
        && isStringArray(candidate.channels)
        && isString(candidate.paymentMode)
        && isString(candidate.trainerSelectionMode)
        && isPositiveInteger(candidate.slotStepMinutes)
        && (candidate.cancellationDeadlineMinutes === undefined || isNonNegativeInteger(candidate.cancellationDeadlineMinutes))
        && isNonNegativeInteger(candidate.minLeadTimeMinutes)
        && isPositiveInteger(candidate.bookingHorizonDays)
        && isNonNegativeInteger(candidate.bufferBeforeMinutes)
        && isNonNegativeInteger(candidate.bufferAfterMinutes)
        && typeof candidate.trainerConfirmationRequired === 'boolean'
        && (candidate.trainerConfirmationDeadlineHours === null
            || candidate.trainerConfirmationDeadlineHours === undefined
            || isPositiveInteger(candidate.trainerConfirmationDeadlineHours))
        && isString(candidate.status)
        && typeof candidate.visibleToCustomers === 'boolean'
        && (candidate.guestCheckoutAvailable === undefined || typeof candidate.guestCheckoutAvailable === 'boolean')
        && isOptionalString(candidate.createdAt)
        && isOptionalString(candidate.updatedAt)
        && isOptionalString(candidate.publishedAt)
        && isOptionalString(candidate.archivedAt)
        && isStringArray(candidate.allowedPassTemplateIds)
        && isStringArray(candidate.allowedInstructorIds)
        && isStringArray(candidate.allowedLocationIds)
        && Array.isArray(candidate.instructors)
        && candidate.instructors.every(isPublicPrivateBookingInstructor)
        && (candidate.locations === undefined
            || (Array.isArray(candidate.locations)
                && candidate.locations.length <= 1
                && candidate.locations.every(isPublicPrivateBookingLocation)))
}

export async function getPublicPrivateBookingOffersStrict(slug: string): Promise<PublicPrivateBookingOffer[]> {
    const normalizedSlug = slug.trim()
    if (!normalizedSlug) return EMPTY_PUBLIC_PRIVATE_BOOKING_OFFERS

    const baseUrl = resolvePublicApiBaseUrl()
    if (!baseUrl) return EMPTY_PUBLIC_PRIVATE_BOOKING_OFFERS

    const url = `${baseUrl}/api/public/partners/${encodeURIComponent(normalizedSlug)}/private-bookings/offers`

    const response = await publicFetch(url, {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
    })
    if (!response.ok) throw new Error(`Public private booking offers request failed: ${response.status}`)

    const body = await response.json()
    return Array.isArray(body) ? body.filter(isPublicPrivateBookingOffer) : EMPTY_PUBLIC_PRIVATE_BOOKING_OFFERS
}

export async function getPublicPrivateBookingSlots(
    slug: string,
    offerId: string,
    query: {endDate: string; locationId?: string | null; startDate: string; trainerId?: string | null},
): Promise<PublicPrivateBookingSlotsResponse | null> {
    const normalizedSlug = slug.trim()
    if (!normalizedSlug) return null

    const baseUrl = resolvePublicApiBaseUrl()
    if (!baseUrl) return null

    const params = new URLSearchParams({
        startDate: query.startDate,
        endDate: query.endDate,
    })
    if (query.trainerId) params.set('trainerId', query.trainerId)
    if (query.locationId) params.set('locationId', query.locationId)

    const url = `${baseUrl}/api/public/partners/${encodeURIComponent(normalizedSlug)}/private-bookings/offers/${encodeURIComponent(offerId)}/slots?${params.toString()}`

    try {
        const response = await publicFetch(url, {
            method: 'GET',
            credentials: 'omit',
            cache: 'no-store',
        })
        if (!response.ok) return null

        const body = await response.json()
        return isPublicPrivateBookingSlotsResponse(body) ? body : null
    } catch {
        return null
    }
}

function isPublicPrivateBookingSlot(value: unknown): value is PublicPrivateBookingSlot {
    if (!value || typeof value !== 'object') return false
    const candidate = value as PublicPrivateBookingSlot
    return isString(candidate.startAt)
        && isString(candidate.endAt)
        && (candidate.instructorId === undefined || candidate.instructorId === null || isString(candidate.instructorId))
        && (candidate.locationId === undefined || candidate.locationId === null || isString(candidate.locationId))
        && isNonNegativeInteger(candidate.availableInstructorCount)
}

function isPublicPrivateBookingSlotsResponse(value: unknown): value is PublicPrivateBookingSlotsResponse {
    if (!value || typeof value !== 'object') return false
    const candidate = value as PublicPrivateBookingSlotsResponse
    return isString(candidate.offerId)
        && isString(candidate.from)
        && isString(candidate.to)
        && Array.isArray(candidate.slots)
        && candidate.slots.every(isPublicPrivateBookingSlot)
}
